import React, { useEffect, useState } from "react";
import { Calendar, Menu, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { BRAND_LOGO_URL } from "@/data/content";

const Nav = () => {
    const { locale, setLocale, t, supportedLocales } = useLanguage();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const navLinks = [
        { key: "services", href: "/#services", label: t.nav.services },
        { key: "artists", href: "/#artists", label: t.nav.artists },
        { key: "gallery", href: "/#gallery", label: t.nav.gallery },
        { key: "process", href: "/#process", label: t.nav.process },
        { key: "faq", href: "/#faq", label: t.nav.faq },
    ];

    return (
        <header
            data-testid="site-nav"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/90 backdrop-blur-md border-b border-border/60 shadow-sm"
                    : "bg-white/50 backdrop-blur-[2px] border-b border-transparent"
            }`}
        >
            <div className="container-x flex items-center justify-between h-[64px]">
                <a href="/" className="flex items-center gap-2.5" data-testid="nav-logo">
                    <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-foreground overflow-hidden">
                        <img
                            src={BRAND_LOGO_URL}
                            alt="BrowsEMS"
                            className="w-8 h-8 object-cover"
                            loading="eager"
                        />
                    </span>
                    <span className="font-heading text-xl text-foreground leading-none">
                        BrowsEMS
                    </span>
                </a>

                <nav className="hidden lg:flex items-center gap-8">
                    {navLinks.map((l) => (
                        <a
                            key={l.key}
                            href={l.href}
                            data-testid={`nav-link-${l.key}`}
                            className="font-body text-[11px] text-foreground/70 hover:text-primary transition-colors tracking-[0.2em] uppercase"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-3">
                    <div
                        className="hidden sm:flex items-center rounded-[0.3rem] border border-border bg-white/80 p-0.5"
                        data-testid="lang-toggle"
                    >
                        {supportedLocales.map((l) => (
                            <button
                                key={l.code}
                                onClick={() => setLocale(l.code)}
                                data-testid={`lang-btn-${l.code}`}
                                className={`px-2.5 py-1 text-[11px] font-body font-medium rounded-[0.2rem] transition-colors ${
                                    locale === l.code
                                        ? "bg-foreground text-background"
                                        : "text-foreground/60 hover:text-foreground"
                                }`}
                            >
                                {l.label}
                            </button>
                        ))}
                    </div>

                    <a
                        href="/#booking"
                        data-testid="nav-book-cta"
                        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-[0.3rem] bg-primary text-white font-body text-xs font-medium hover:bg-primary/90 transition-colors"
                    >
                        <Calendar size={13} />
                        {t.nav.book}
                    </a>

                    <button
                        aria-label="Menu"
                        data-testid="nav-mobile-toggle"
                        className="lg:hidden p-2 rounded-[0.3rem] hover:bg-secondary transition-colors"
                        onClick={() => setMobileOpen((v) => !v)}
                    >
                        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden border-t border-border bg-white/95 backdrop-blur-md"
                >
                    <nav className="container-x py-4 flex flex-col gap-1">
                        {navLinks.map((l) => (
                            <a
                                key={l.key}
                                href={l.href}
                                onClick={() => setMobileOpen(false)}
                                className="px-3 py-3 font-body text-sm text-foreground hover:bg-secondary rounded-[0.3rem] transition-colors"
                                data-testid={`mobile-link-${l.key}`}
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href="/#booking"
                            onClick={() => setMobileOpen(false)}
                            className="mt-2 px-4 py-3 rounded-[0.3rem] bg-primary text-white font-body text-sm font-medium inline-flex items-center gap-2 justify-center"
                            data-testid="mobile-book-cta"
                        >
                            <Calendar size={15} />
                            {t.nav.book}
                        </a>
                        <div className="flex items-center gap-2 pt-2">
                            {supportedLocales.map((l) => (
                                <button
                                    key={l.code}
                                    onClick={() => setLocale(l.code)}
                                    data-testid={`mobile-lang-${l.code}`}
                                    className={`px-4 py-1.5 text-xs font-body font-medium rounded-[0.3rem] ${
                                        locale === l.code
                                            ? "bg-foreground text-background"
                                            : "bg-secondary text-foreground/80"
                                    }`}
                                >
                                    {l.label}
                                </button>
                            ))}
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Nav;
